import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { Calendar, Bell, ClipboardList } from "lucide-react";
import flytLogo from "@/assets/flyt-logo.svg";

const GENERIC_ERROR = "Denne invitasjonen er ugyldig eller har utløpt.";

export default function InviteAccept() {
  const { code } = useParams<{ code: string }>();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [status, setStatus] = useState<"loading" | "ready" | "accepting" | "error" | "success">("loading");
  const [childNames, setChildNames] = useState<string[]>([]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate(`/register?redirect=/invite/${code}`, { replace: true });
      return;
    }
    async function check() {
      if (!code) {
        setStatus("error");
        return;
      }
      const { data } = await supabase
        .rpc("validate_invite_code", { _invite_code: code });

      if (!data) {
        setStatus("error");
      } else {
        // data is jsonb: { id, child_names }
        const parsed = typeof data === "string" ? JSON.parse(data) : data;
        setChildNames(parsed.child_names || []);
        setStatus("ready");
      }
    }
    check();
  }, [user, authLoading, code, navigate]);

  async function handleAccept() {
    if (!user || !code) return;
    setStatus("accepting");

    try {
      const { data, error } = await supabase
        .rpc("accept_family_invite", { _invite_code: code });

      if (error) {
        console.error("Accept invite error:", error);
        toast.error(GENERIC_ERROR);
        setStatus("error");
        return;
      }

      await queryClient.invalidateQueries({ queryKey: ["family-memberships"] });
      queryClient.invalidateQueries({ queryKey: ["child-coparents-all"] });
      queryClient.invalidateQueries({ queryKey: ["all-child-coparents"] });
      queryClient.invalidateQueries({ queryKey: ["children"] });
      queryClient.invalidateQueries({ queryKey: ["all-children"] });
      setStatus("success");
      toast.success("Du er nå med!");
      navigate("/onboarding?invited=true", { replace: true });
    } catch {
      toast.error(GENERIC_ERROR);
      setStatus("error");
    }
  }

  if (authLoading || status === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <p className="text-muted-foreground">Laster…</p>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <Card className="w-full max-w-sm text-center">
          <CardContent className="p-6 space-y-4">
            <div className="text-4xl">❌</div>
            <h2 className="text-lg font-semibold text-foreground">{GENERIC_ERROR}</h2>
            <Link to="/login">
              <Button variant="outline" className="w-full">
                Gå til innlogging
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const childLabel = childNames.length > 0
    ? childNames.join(" og ")
    : "barna";

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm space-y-5">
        {/* Logo */}
        <div className="text-center">
          <img
            src={flytLogo}
            alt="Flyt"
            className="mx-auto h-10 w-auto"
          />
        </div>

        <Card className="w-full">
          <CardContent className="p-6 space-y-5">
            {/* Main message */}
            <div className="text-center space-y-2">
              <div className="text-4xl">🤝</div>
              <h1 className="text-xl font-semibold text-foreground">
                Du er invitert til å følge opp {childLabel} sammen
              </h1>
              <p className="text-sm text-muted-foreground">
                Den andre forelderen bruker allerede Flyt for å organisere hverdagen.
              </p>
            </div>

            {/* Free messaging */}
            <div className="rounded-xl bg-primary/5 border border-primary/20 p-4 text-center">
              <p className="text-sm font-medium text-foreground">
                ✨ Helt gratis for deg
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Ingen abonnement, ingen betalingsinformasjon – du får full tilgang til barna du er invitert til.
              </p>
            </div>

            {/* Value props */}
            <div className="space-y-2.5">
              <div className="flex items-start gap-3">
                <ClipboardList className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-foreground">Felles oppgaver</p>
                  <p className="text-xs text-muted-foreground">Se og opprett oppgaver – alltid oppdatert for begge</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Bell className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-foreground">Påminnelser</p>
                  <p className="text-xs text-muted-foreground">Delte påminnelser som følger barnet – uansett hvem som har ansvar</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Calendar className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-foreground">Kalender og oversikt</p>
                  <p className="text-xs text-muted-foreground">Felles kalender med aktiviteter, ukeplan og samværsoversikt</p>
                </div>
              </div>
            </div>

            {/* CTA */}
            <Button
              onClick={handleAccept}
              disabled={status === "accepting"}
              className="w-full h-12 text-base"
            >
              {status === "accepting" ? "Godtar…" : "Godta invitasjon"}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
